import React from 'react';
import { Button } from './ui/button';
import {
  LayoutDashboard, ShoppingCart, Gamepad2, Clock,
  ClipboardList, Users, CalendarCheck, BarChart3,
  DollarSign, Package, Heart, Handshake,
  MessageCircle, Megaphone, Database, User, FileText, CreditCard, Globe, Smartphone
} from 'lucide-react';
import { useRelatorioBadge } from '../hooks/useRelatorioBadge';

const AdminNavigationTabs = ({
  activeTab,
  handleTabClick,
  user,
  acessoRestrito,
  horariosNaoVistos,
  perfisPendentes,
  trocasPendentes,
  feriasPendentes,
  avaliacoesPendentes,
  mensagensNaoLidas,
  perfilIncompleto,
  documentosPendentes = 0,
  boxNovasCount = 0,
  clientesAlertas = 0,
  carregarPerfisPendentes,
  carregarMensagensNaoLidas,
  setScrollChatToUnread,
}) => {
  const totalColabNotifs = perfisPendentes + trocasPendentes + feriasPendentes + avaliacoesPendentes;
  const { badge: rtBadge } = useRelatorioBadge();

  return (
    <div className="flex flex-nowrap md:flex-wrap gap-1 sm:gap-2 mt-2 sm:mt-4 md:pb-0 overflow-x-auto md:overflow-x-visible pb-2 -mx-2 px-2 md:mx-0 md:px-0 scrollbar-thin md:scrollbar-none snap-x snap-proximity">
      {/* 1. Dashboard */}
      {!acessoRestrito && (
        <Button variant={activeTab === 'dashboard' ? 'default' : 'outline'} onClick={(e) => handleTabClick('dashboard', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" title="Duplo clique para abrir em nova aba" data-testid="tab-dashboard">
          <LayoutDashboard className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Dashboard
        </Button>
      )}

      {/* 2. Operacao (consulta operacional — sem sub-tabs) */}
      {!acessoRestrito && (
        <Button variant={activeTab === 'operacao' ? 'default' : 'outline'} onClick={(e) => handleTabClick('operacao', e)} className={`whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 ${activeTab !== 'operacao' ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : 'bg-amber-600 hover:bg-amber-700'}`} title="Consulta operacional" data-testid="tab-operacao">
          <CreditCard className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Consulta Operacional
        </Button>
      )}

      {/* 3. Vendas */}
      {!acessoRestrito && (
        <Button variant={activeTab === 'vendas' ? 'default' : 'outline'} onClick={(e) => handleTabClick('vendas', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" title="Duplo clique para abrir em nova aba" data-testid="tab-vendas">
          <ShoppingCart className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Vendas
        </Button>
      )}

      {/* 3. Jogos */}
      {!acessoRestrito && (
        <Button variant={activeTab === 'jogos' ? 'default' : 'outline'} onClick={(e) => handleTabClick('jogos', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" title="Duplo clique para abrir em nova aba" data-testid="tab-jogos">
          <Gamepad2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Jogos
        </Button>
      )}

      {/* 4. Registo Ponto */}
      {!acessoRestrito && (
        <Button variant={activeTab === 'ponto' ? 'default' : 'outline'} onClick={(e) => handleTabClick('ponto', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" title="Duplo clique para abrir em nova aba" data-testid="tab-ponto">
          <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Ponto
        </Button>
      )}

      {/* 5. Relatório de Turno (iter289) */}
      {!acessoRestrito && (user.role === 'admin' || user.role === 'responsavel') && (
        <Button variant={activeTab === 'gerir-tarefas' ? 'default' : 'outline'} onClick={(e) => handleTabClick('gerir-tarefas', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 relative" title="Comunicação de turno: indicações, pendentes, ocorrências" data-testid="tab-relatorio-turno">
          <ClipboardList className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Relatório de Turno
          {rtBadge.count > 0 && (
            <span
              className={`ml-1 inline-flex items-center justify-center text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 ${
                rtBadge.urgentes_por_ler > 0 ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'
              }`}
              data-testid="rt-menu-badge"
              title={`${rtBadge.urgentes_por_ler} indicações urgentes por ler · ${rtBadge.pendentes_arrastados} pendentes arrastados`}
            >
              {rtBadge.count > 99 ? '99+' : rtBadge.count}
            </span>
          )}
        </Button>
      )}

      {/* 6. Documentos */}
      {!acessoRestrito && (user.role === 'admin' || user.role === 'responsavel') && (
        <Button
          variant={activeTab === 'documentos' ? 'default' : 'outline'}
          onClick={(e) => handleTabClick('documentos', e)}
          className="whitespace-nowrap relative text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
          title="Comunicados, procedimentos, normas, atas e base documental"
          data-testid="tab-documentos"
        >
          <FileText className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Documentos
          {documentosPendentes > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {documentosPendentes}
            </span>
          )}
        </Button>
      )}

      {/* 6. Colaboradores (absorve Faltas) */}
      {!acessoRestrito && (
        <Button
          variant={activeTab === 'colaboradores' ? 'default' : 'outline'}
          onClick={(e) => handleTabClick('colaboradores', e)}
          className="whitespace-nowrap relative text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
          title="Duplo clique para abrir em nova aba"
          data-testid="tab-colaboradores"
        >
          <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Colaboradores
          {user.role === 'admin' && totalColabNotifs > 0 && (
            <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {totalColabNotifs}
            </span>
          )}
        </Button>
      )}

      {/* 7. Horarios (escalas mensais) */}
      <Button
        variant={activeTab === 'horario' ? 'default' : 'outline'}
        onClick={(e) => handleTabClick('horario', e)}
        className="whitespace-nowrap relative text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
        title="Horarios mensais e escalas da equipa"
        data-testid="tab-horario"
      >
        <CalendarCheck className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
        Horarios
        {horariosNaoVistos > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {horariosNaoVistos}
          </span>
        )}
      </Button>

      {/* 7.5. Agenda (pessoal) */}
      {!acessoRestrito && user.role === 'admin' && (
        <Button
          variant={activeTab === 'agenda' ? 'default' : 'outline'}
          onClick={(e) => handleTabClick('agenda', e)}
          className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
          title="Agenda pessoal, compromissos e lembretes"
          data-testid="tab-agenda"
        >
          <CalendarCheck className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Agenda
        </Button>
      )}

      {/* 8. Monitorizacao (absorve Analise) */}
      {!acessoRestrito && user.role === 'admin' && (
        <Button variant={activeTab === 'monitorizacao' ? 'default' : 'outline'} onClick={(e) => handleTabClick('monitorizacao', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" title="Duplo clique para abrir em nova aba" data-testid="tab-monitorizacao">
          <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Monitorizacao
        </Button>
      )}

      {/* 9. Fluxo de Caixa */}
      {!acessoRestrito && user.role === 'admin' && (
        <Button variant={activeTab === 'fluxo-caixa' ? 'default' : 'outline'} onClick={(e) => handleTabClick('fluxo-caixa', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" title="Duplo clique para abrir em nova aba" data-testid="tab-fluxo-caixa">
          <DollarSign className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Fluxo de Caixa
        </Button>
      )}

      {/* 10. Produtos (Artigos + Validades) */}
      {!acessoRestrito && (
        <Button variant={activeTab === 'produtos' ? 'default' : 'outline'} onClick={(e) => handleTabClick('produtos', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" title="Duplo clique para abrir em nova aba" data-testid="tab-produtos">
          <Package className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Produtos
        </Button>
      )}

      {/* 11. Clientes (Gestao + Reconciliacao + RFM + XD) */}
      {!acessoRestrito && (
        <Button variant={activeTab === 'clientes' ? 'default' : 'outline'} onClick={(e) => handleTabClick('clientes', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2 relative" title="Gestao de Clientes" data-testid="tab-clientes">
          <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Clientes
          {clientesAlertas > 0 && (
            <span
              className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full"
              title={`${clientesAlertas} alerta${clientesAlertas === 1 ? '' : 's'}: clientes em risco ou sem NIF relevantes`}
              data-testid="badge-clientes-alertas"
            >
              {clientesAlertas > 99 ? '99+' : clientesAlertas}
            </span>
          )}
        </Button>
      )}

      {/* 12. Fidelizacao */}
      {!acessoRestrito && user.role === 'admin' && (
        <Button variant={activeTab === 'fidelizacao' ? 'default' : 'outline'} onClick={(e) => handleTabClick('fidelizacao', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" title="Fidelizacao" data-testid="tab-fidelizacao">
          <Heart className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Fidelizacao
        </Button>
      )}

      {/* 12.1 App Cliente — tab principal autónoma (admin) */}
      {!acessoRestrito && user.role === 'admin' && (
        <Button variant={activeTab === 'app-cliente' ? 'default' : 'outline'} onClick={(e) => handleTabClick('app-cliente', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" title="App Cliente" data-testid="tab-app-cliente">
          <Smartphone className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          App Cliente
        </Button>
      )}

      {/* 12.5 Campanhas em Vigor — visualização rápida (apenas para responsável/colaborador,
          admin já acede via Fidelização → Em Vigor; evita duplicação no menu) */}
      {!acessoRestrito && user.role === 'responsavel' && (
        <Button variant={activeTab === 'campanhas-em-vigor' ? 'default' : 'outline'} onClick={(e) => handleTabClick('campanhas-em-vigor', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" title="Campanhas em Vigor (apenas visualização)" data-testid="tab-campanhas-em-vigor">
          <Megaphone className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Em Vigor
        </Button>
      )}

      {/* 13. Comercial (Parcerias + Campanhas + Motor) */}
      {!acessoRestrito && (
        <Button variant={activeTab === 'comercial' ? 'default' : 'outline'} onClick={(e) => handleTabClick('comercial', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" title="Duplo clique para abrir em nova aba" data-testid="tab-comercial">
          <Handshake className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Comercial
        </Button>
      )}

      {/* 14. Chat (também disponível em acesso remoto) */}
      <Button
        variant={activeTab === 'chat' ? 'default' : 'outline'}
        onClick={(e) => {
          handleTabClick('chat', e);
          setScrollChatToUnread(true);
        }}
        className="whitespace-nowrap relative text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
        title="Duplo clique para abrir em nova aba"
        data-testid="tab-chat"
      >
        <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
        Chat
        {mensagensNaoLidas > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {mensagensNaoLidas}
          </span>
        )}
      </Button>

      {/* 15. Operacional Hoje (Avisos + Monitorização) - admin ou responsavel */}
      {!acessoRestrito && (user.role === 'admin' || user.role === 'responsavel') && (
        <Button variant={activeTab === 'controlo' ? 'default' : 'outline'} onClick={(e) => handleTabClick('controlo', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" title="Operacional Hoje — Avisos e Monitorização" data-testid="tab-controlo">
          <Megaphone className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Operacional Hoje
        </Button>
      )}

      {/* 16. Sistema - apenas admin */}
      {!acessoRestrito && user.role === 'admin' && (
        <Button variant={activeTab === 'sistema' ? 'default' : 'outline'} onClick={(e) => handleTabClick('sistema', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" title="Sistema e Configuracoes" data-testid="tab-sistema">
          <Database className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Sistema
        </Button>
      )}

      {/* 17. Website - apenas admin */}
      {!acessoRestrito && user.role === 'admin' && (
        <Button variant={activeTab === 'website' ? 'default' : 'outline'} onClick={(e) => handleTabClick('website', e)} className="whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" title="Gestao do Website Publico" data-testid="tab-website">
          <Globe className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Website
        </Button>
      )}

      {/* 18. Box Janeira - admin / responsavel / colaborador */}
      {!acessoRestrito && (user.role === 'admin' || user.role === 'responsavel' || user.role === 'colaborador') && (
        <Button variant={activeTab === 'box' ? 'default' : 'outline'} onClick={(e) => handleTabClick('box', e)} className="whitespace-nowrap relative text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2" title="Box Janeira - Reservas anti-desperdicio" data-testid="tab-box">
          <Package className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Box
          {boxNovasCount > 0 && (
            <span
              className="ml-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold h-4 min-w-4 px-1 animate-pulse"
              data-testid="tab-box-new-badge"
            >
              {boxNovasCount > 9 ? '9+' : boxNovasCount}
            </span>
          )}
        </Button>
      )}

      {/* Meu Perfil - para responsaveis e colaboradores */}
      {!acessoRestrito && (user.role === 'responsavel' || user.role === 'colaborador') && (
        <Button
          variant={activeTab === 'meu-perfil' ? 'default' : 'outline'}
          onClick={(e) => handleTabClick('meu-perfil', e)}
          className="whitespace-nowrap relative text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2"
          title="Duplo clique para abrir em nova aba"
          data-testid="tab-meu-perfil"
        >
          <User className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          Meu Perfil
          {perfilIncompleto && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              !
            </span>
          )}
        </Button>
      )}
    </div>
  );
};

export default AdminNavigationTabs;
